import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapPin, X, Clock, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CircuitCard from "@/components/CircuitCard";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Duration categories for treks
const durationCategories = {
  court: { min: 0, max: 5, label: { fr: "Courts (2-5 jours)", en: "Short (2-5 days)" } },
  moyen: { min: 6, max: 9, label: { fr: "Moyens (6-9 jours)", en: "Medium (6-9 days)" } },
  long: { min: 10, max: 99, label: { fr: "Longs (10+ jours)", en: "Long (10+ days)" } },
};

// City labels (for SEO)
const cityLabels = {
  marrakech: "Marrakech",
  ouarzazate: "Ouarzazate", 
  agadir: "Agadir",
  zagora: "Zagora",
};

// City SEO presentations with multilingual content
const cityPresentations = {
  marrakech: {
    title: {
      fr: "Départ de Marrakech : Aventures 4x4 dans le Désert Marocain",
      en: "Departure from Marrakech: 4x4 Adventures in the Moroccan Desert",
      es: "Salida desde Marrakech: Aventuras 4x4 en el Desierto Marroquí",
      nl: "Vertrek vanuit Marrakech: 4x4 Avonturen in de Marokkaanse Woestijn",
      ar: "انطلاقاً من مراكش: مغامرات 4x4 في الصحراء المغربية"
    },
    description: {
      fr: "Partez à l'aventure depuis Marrakech et explorez les paysages spectaculaires du Sahara marocain. Nos circuits 4x4 vous emmènent à travers les vallées du Drâa, les dunes de Zagora, et des oasis cachées. Découvrez la culture berbère et vivez une expérience unique à travers les paysages magnifiques du désert marocain. Un voyage inoubliable dans les dunes de Merzouga et au cœur du Sahara vous attend.",
      en: "Embark on an adventure from Marrakech and explore the spectacular landscapes of the Moroccan Sahara. Our 4x4 tours take you through the Draa valleys, Zagora dunes, and hidden oases. Discover Berber culture and live a unique experience through the magnificent landscapes of the Moroccan desert. An unforgettable journey to the Merzouga dunes and the heart of the Sahara awaits you.",
      es: "Embárcate en una aventura desde Marrakech y explora los espectaculares paisajes del Sáhara marroquí. Nuestros circuitos 4x4 te llevan a través de los valles del Draa, las dunas de Zagora y oasis escondidos. Descubre la cultura bereber y vive una experiencia única a través de los magníficos paisajes del desierto marroquí. Un viaje inolvidable a las dunas de Merzouga y al corazón del Sáhara te espera.",
      nl: "Begin een avontuur vanuit Marrakech en verken de spectaculaire landschappen van de Marokkaanse Sahara. Onze 4x4-tours nemen je mee door de Draa-valleien, de duinen van Zagora en verborgen oases. Ontdek de Berbercultuur en beleef een unieke ervaring door de prachtige landschappen van de Marokkaanse woestijn. Een onvergetelijke reis naar de duinen van Merzouga en het hart van de Sahara wacht op je.",
      ar: "انطلق في مغامرة من مراكش واستكشف المناظر الطبيعية الخلابة للصحراء المغربية. تأخذك جولاتنا بسيارات الدفع الرباعي عبر وديان درعة وكثبان زاكورة والواحات المخفية. اكتشف الثقافة الأمازيغية وعش تجربة فريدة من نوعها عبر المناظر الطبيعية الرائعة للصحراء المغربية. رحلة لا تُنسى إلى كثبان مرزوقة وقلب الصحراء في انتظارك."
    },
    keywords: ["aventure 4x4 Marrakech", "voyage dans le désert Maroc", "circuits Sahara Marrakech", "dunes Merzouga", "explorer le désert marocain", "coucher de soleil désert Marrakech"]
  },
  ouarzazate: {
    title: {
      fr: "Départ de Ouarzazate : Aventures 4x4 dans le Sahara",
      en: "Departure from Ouarzazate: 4x4 Adventures in the Sahara",
      es: "Salida desde Ouarzazate: Aventuras 4x4 en el Sáhara",
      nl: "Vertrek vanuit Ouarzazate: 4x4 Avonturen in de Sahara",
      ar: "انطلاقاً من ورزازات: مغامرات 4x4 في الصحراء"
    },
    description: {
      fr: "Ouarzazate est la porte d'entrée idéale pour des aventures en 4x4 à travers les paysages impressionnants du Sahara. Découvrez les kasbahs légendaires, les oasis verdoyantes, et les dunes infinies de Merzouga. Traversez les montagnes de l'Atlas et plongez dans l'authenticité de la culture berbère tout en explorant le désert marocain. Un voyage hors du commun vous attend dans le Sahara.",
      en: "Ouarzazate is the ideal gateway for 4x4 adventures through the impressive landscapes of the Sahara. Discover legendary kasbahs, lush oases, and the endless dunes of Merzouga. Cross the Atlas Mountains and immerse yourself in the authenticity of Berber culture while exploring the Moroccan desert. An extraordinary journey awaits you in the Sahara.",
      es: "Ouarzazate es la puerta de entrada ideal para aventuras en 4x4 a través de los impresionantes paisajes del Sáhara. Descubre kasbahs legendarios, oasis verdes y las dunas infinitas de Merzouga. Cruza las montañas del Atlas y sumérgete en la autenticidad de la cultura bereber mientras exploras el desierto marroquí. Un viaje extraordinario te espera en el Sáhara.",
      nl: "Ouarzazate is de ideale toegangspoort voor 4x4-avonturen door de indrukwekkende landschappen van de Sahara. Ontdek legendarische kasba's, weelderige oases en de eindeloze duinen van Merzouga. Doorkruis het Atlasgebergte en dompel jezelf onder in de authenticiteit van de Berbercultuur terwijl je de Marokkaanse woestijn verkent. Een buitengewone reis wacht op je in de Sahara.",
      ar: "ورزازات هي البوابة المثالية لمغامرات الدفع الرباعي عبر المناظر الطبيعية المذهلة للصحراء. اكتشف القصبات الأسطورية والواحات الخضراء والكثبان اللانهائية في مرزوقة. اعبر جبال الأطلس وانغمس في أصالة الثقافة الأمازيغية أثناء استكشاف الصحراء المغربية. رحلة استثنائية تنتظرك في الصحراء."
    },
    keywords: ["circuits 4x4 Ouarzazate", "aventure dans le désert Maroc", "voyage Merzouga Ouarzazate", "exploration désert Sahara", "voyage culturel Ouarzazate"]
  },
  agadir: {
    title: {
      fr: "Départ d'Agadir : Aventures 4x4 dans le Sud Marocain",
      en: "Departure from Agadir: 4x4 Adventures in Southern Morocco",
      es: "Salida desde Agadir: Aventuras 4x4 en el Sur de Marruecos",
      nl: "Vertrek vanuit Agadir: 4x4 Avonturen in Zuid-Marokko",
      ar: "انطلاقاً من أكادير: مغامرات 4x4 في جنوب المغرب"
    },
    description: {
      fr: "À partir d'Agadir, explorez les paysages désertiques fascinants du sud marocain en 4x4. Traversez les montagnes de l'Atlas, les vastes étendues du Sahara et découvrez les oasis cachées. De Merzouga à Zagora, vivez une aventure authentique dans le désert marocain, imprégnée de culture berbère et de panoramas époustouflants.",
      en: "From Agadir, explore the fascinating desert landscapes of southern Morocco by 4x4. Cross the Atlas Mountains, the vast expanses of the Sahara and discover hidden oases. From Merzouga to Zagora, experience an authentic adventure in the Moroccan desert, steeped in Berber culture and breathtaking panoramas.",
      es: "Desde Agadir, explora los fascinantes paisajes desérticos del sur de Marruecos en 4x4. Cruza las montañas del Atlas, las vastas extensiones del Sáhara y descubre oasis escondidos. De Merzouga a Zagora, vive una aventura auténtica en el desierto marroquí, impregnada de cultura bereber y panoramas impresionantes.",
      nl: "Verken vanuit Agadir de fascinerende woestijnlandschappen van Zuid-Marokko met een 4x4. Doorkruis het Atlasgebergte, de uitgestrekte vlakten van de Sahara en ontdek verborgen oases. Van Merzouga tot Zagora, beleef een authentiek avontuur in de Marokkaanse woestijn, doordrenkt met Berbercultuur en adembenemende panorama's.",
      ar: "انطلاقاً من أكادير، استكشف المناظر الطبيعية الصحراوية الرائعة في جنوب المغرب بسيارة الدفع الرباعي. اعبر جبال الأطلس، والمساحات الشاسعة من الصحراء واكتشف الواحات المخفية. من مرزوقة إلى زاكورة، عش مغامرة أصيلة في الصحراء المغربية، مشبعة بالثقافة الأمازيغية والمناظر البانورامية الخلابة."
    },
    keywords: ["aventure 4x4 Agadir", "voyage dans le désert Agadir", "Sahara depuis Agadir", "circuits Merzouga Agadir", "dunes Chigaga Agadir"]
  },
  zagora: {
    title: {
      fr: "Départ de Zagora : Aventures 4x4 à Travers le Désert",
      en: "Departure from Zagora: 4x4 Adventures Through the Desert",
      es: "Salida desde Zagora: Aventuras 4x4 a Través del Desierto",
      nl: "Vertrek vanuit Zagora: 4x4 Avonturen Door de Woestijn",
      ar: "انطلاقاً من زاكورة: مغامرات 4x4 عبر الصحراء"
    },
    description: {
      fr: "Zagora, porte du Sahara, vous invite à une aventure 4x4 à travers les dunes dorées et les oasis cachées. Explorez les paysages fascinants du désert marocain, découvrez la culture nomade et vivez l'authenticité du Sahara. Une aventure inoubliable vous attend à travers les étendues infinies du désert.",
      en: "Zagora, gateway to the Sahara, invites you to a 4x4 adventure through golden dunes and hidden oases. Explore the fascinating landscapes of the Moroccan desert, discover nomadic culture and experience the authenticity of the Sahara. An unforgettable adventure awaits you through the endless expanses of the desert.",
      es: "Zagora, puerta del Sáhara, te invita a una aventura en 4x4 a través de dunas doradas y oasis escondidos. Explora los fascinantes paisajes del desierto marroquí, descubre la cultura nómada y vive la autenticidad del Sáhara. Una aventura inolvidable te espera a través de las extensiones infinitas del desierto.",
      nl: "Zagora, de poort naar de Sahara, nodigt je uit voor een 4x4-avontuur door gouden duinen en verborgen oases. Verken de fascinerende landschappen van de Marokkaanse woestijn, ontdek de nomadische cultuur en ervaar de authenticiteit van de Sahara. Een onvergetelijk avontuur wacht op je door de eindeloze uitgestrektheid van de woestijn.",
      ar: "زاكورة، بوابة الصحراء، تدعوك إلى مغامرة بسيارة الدفع الرباعي عبر الكثبان الذهبية والواحات المخفية. استكشف المناظر الطبيعية الرائعة للصحراء المغربية، واكتشف الثقافة البدوية وعش أصالة الصحراء. مغامرة لا تُنسى تنتظرك عبر المساحات اللانهائية من الصحراء."
    },
    keywords: ["circuits Zagora désert", "aventure 4x4 Zagora", "explorer Sahara Zagora", "voyage désert Zagora", "dunes Zagora Maroc"]
  }
};

const CircuitsPage = ({ type: initialType }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [circuits, setCircuits] = useState([]);
  const [allCircuits, setAllCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialType || "all");
  const [cityFilter, setCityFilter] = useState("all");
  const [durationFilter, setDurationFilter] = useState("all");
  const [departureCities, setDepartureCities] = useState([]);

  // Read query params on mount
  useEffect(() => {
    const cityParam = searchParams.get("city");
    const durationParam = searchParams.get("duration");
    
    if (cityParam && cityLabels[cityParam.toLowerCase()]) {
      setCityFilter(cityLabels[cityParam.toLowerCase()]);
    }
    
    if (durationParam && durationCategories[durationParam]) {
      setDurationFilter(durationParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [circuitsRes, citiesRes] = await Promise.all([
          axios.get(`${API}/circuits`),
          axios.get(`${API}/circuits/departure-cities/list`),
        ]);
        setAllCircuits(circuitsRes.data);
        setDepartureCities(citiesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (initialType) {
      setFilter(initialType);
    }
  }, [initialType]);

  // Apply filters
  useEffect(() => {
    let filtered = [...allCircuits];
    
    if (filter !== "all") {
      filtered = filtered.filter((c) => c.type === filter);
    }
    
    if (cityFilter !== "all") {
      filtered = filtered.filter((c) => 
        c.departure_city?.toLowerCase() === cityFilter.toLowerCase()
      );
    }
    
    if (durationFilter !== "all" && durationCategories[durationFilter]) {
      const { min, max } = durationCategories[durationFilter];
      filtered = filtered.filter((c) => 
        c.duration_days >= min && c.duration_days <= max
      );
    }
    
    setCircuits(filtered);
  }, [filter, cityFilter, durationFilter, allCircuits]);

  const clearFilters = () => {
    setFilter(initialType || "all");
    setCityFilter("all");
    setDurationFilter("all");
    setSearchParams({});
  };

  const activeFiltersCount = 
    (filter !== "all" && filter !== initialType ? 1 : 0) + 
    (cityFilter !== "all" ? 1 : 0) + 
    (durationFilter !== "all" ? 1 : 0);

  // Generate SEO title and description based on filters
  const getSEOTitle = () => {
    if (initialType === "4x4" && cityFilter !== "all") {
      const cityKey = cityFilter.toLowerCase();
      const cityData = cityPresentations[cityKey];
      if (cityData) {
        return `${cityData.title[lang] || cityData.title.fr} | XploreSahara`;
      }
      return lang === "fr" 
        ? `Circuits 4x4 Départ de ${cityFilter} - Désert Sahara Maroc | XploreSahara`
        : `4x4 Tours from ${cityFilter} - Sahara Desert Morocco | XploreSahara`;
    }
    if (initialType === "trekking" && durationFilter !== "all") {
      const durLabel = durationCategories[durationFilter]?.label?.[lang] || durationCategories[durationFilter]?.label?.fr;
      return lang === "fr"
        ? `Treks ${durLabel} - Randonnée Sahara Maroc | XploreSahara`
        : `${durLabel} Treks - Sahara Hiking Morocco | XploreSahara`;
    }
    return undefined; // Use default from SEOHead
  };

  const getSEODescription = () => {
    if (initialType === "4x4" && cityFilter !== "all") {
      const cityKey = cityFilter.toLowerCase();
      const cityData = cityPresentations[cityKey];
      if (cityData) {
        return cityData.description[lang] || cityData.description.fr;
      }
      return lang === "fr"
        ? `Découvrez nos circuits 4x4 au départ de ${cityFilter}. Aventure dans le désert du Sahara, dunes d'Erg Chegaga, bivouac sous les étoiles. Réservez maintenant !`
        : `Discover our 4x4 tours from ${cityFilter}. Adventure in the Sahara desert, Erg Chegaga dunes, overnight camping. Book now!`;
    }
    if (initialType === "trekking" && durationFilter !== "all") {
      return lang === "fr"
        ? `Treks et randonnées dans le Sahara marocain. Marche dans les dunes, méharée, bivouac nomade. De ${durationCategories[durationFilter]?.min || 2} à ${durationCategories[durationFilter]?.max || 13} jours.`
        : `Treks and hikes in the Moroccan Sahara. Dune walking, camel ride, nomad camping. From ${durationCategories[durationFilter]?.min || 2} to ${durationCategories[durationFilter]?.max || 13} days.`;
    }
    return undefined;
  };

  const getSEOKeywords = () => {
    const cityKey = cityFilter?.toLowerCase();
    const cityData = cityPresentations[cityKey];
    const baseKeywords = [
      cityFilter !== "all" ? `circuit ${cityFilter}` : null,
      initialType === "4x4" ? "circuit 4x4 désert" : null,
      initialType === "trekking" ? "trek sahara randonnée" : null,
    ].filter(Boolean);
    
    if (cityData?.keywords) {
      return [...baseKeywords, ...cityData.keywords];
    }
    return baseKeywords;
  };

  // Get city presentation for display
  const getCityPresentation = () => {
    if (cityFilter === "all") return null;
    const cityKey = cityFilter.toLowerCase();
    return cityPresentations[cityKey] || null;
  };

  // Get page title based on type
  const getPageTitle = () => {
    if (initialType === "4x4") {
      return lang === "fr" ? "Circuits 4x4" : "4x4 Tours";
    }
    if (initialType === "trekking") {
      return lang === "fr" ? "Treks & Randonnées" : "Treks & Hikes";
    }
    return t("circuits.title");
  };

  // Get subtitle based on filters
  const getSubtitle = () => {
    if (cityFilter !== "all") {
      return lang === "fr" ? `Départ de ${cityFilter}` : `Departure from ${cityFilter}`;
    }
    if (durationFilter !== "all") {
      return durationCategories[durationFilter]?.label?.[lang] || durationCategories[durationFilter]?.label?.fr;
    }
    return t("circuits.subtitle");
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="circuits-page">
      <SEOHead 
        customTitle={getSEOTitle()} 
        customDescription={getSEODescription()}
        customKeywords={getSEOKeywords()}
      />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="text-primary font-medium tracking-widest uppercase text-sm">
            {getSubtitle()}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-2">
            {getPageTitle()}
          </h1>
          {/* Introduction text or City Presentation */}
          {getCityPresentation() ? (
            <div className="mt-8 max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                {getCityPresentation().title[lang] || getCityPresentation().title.fr}
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                {getCityPresentation().description[lang] || getCityPresentation().description.fr}
              </p>
              <p className="text-sm text-primary mt-4">
                {circuits.length} {lang === "fr" ? "circuits disponibles" : lang === "es" ? "circuitos disponibles" : lang === "nl" ? "circuits beschikbaar" : lang === "ar" ? "دوائر متوفرة" : "circuits available"}
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground mt-6 max-w-3xl mx-auto text-lg leading-relaxed">
              {t("circuits.intro")}
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8">
          {/* Type Filter - Only show if no initial type */}
          {!initialType && (
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="bg-muted p-1 rounded-full">
                <TabsTrigger
                  value="all"
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="filter-all"
                >
                  {t("circuits.all")}
                </TabsTrigger>
                <TabsTrigger
                  value="4x4"
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="filter-4x4"
                >
                  {t("circuits.type4x4")}
                </TabsTrigger>
                <TabsTrigger
                  value="trekking"
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="filter-trekking"
                >
                  {t("circuits.typeTrekking")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* City Filter - Only for 4x4 */}
          {(initialType === "4x4" || filter === "4x4") && (
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-[220px] rounded-full" data-testid="city-filter">
                <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder={lang === "fr" ? "Ville de départ" : "Departure city"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {lang === "fr" ? "Toutes les villes" : "All cities"}
                </SelectItem>
                {departureCities.map((city) => (
                  <SelectItem key={city} value={city}>
                    {lang === "fr" ? `Départ de ${city}` : `From ${city}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Duration Filter - Only for trekking */}
          {(initialType === "trekking" || filter === "trekking") && (
            <Select value={durationFilter} onValueChange={setDurationFilter}>
              <SelectTrigger className="w-[220px] rounded-full" data-testid="duration-filter">
                <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder={lang === "fr" ? "Durée du trek" : "Trek duration"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {lang === "fr" ? "Toutes les durées" : "All durations"}
                </SelectItem>
                {Object.entries(durationCategories).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>
                    {label[lang] || label.fr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Clear filters button */}
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4 mr-1" />
              {lang === "fr" ? "Effacer les filtres" : "Clear filters"}
            </Button>
          )}
        </div>

        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
            <span className="text-sm text-muted-foreground">
              {lang === "fr" ? "Filtres actifs:" : "Active filters:"}
            </span>
            {filter !== "all" && filter !== initialType && (
              <Badge variant="secondary" className="gap-1">
                <Compass className="h-3 w-3" />
                {filter === "4x4" ? "4x4" : "Trekking"}
                <button onClick={() => setFilter(initialType || "all")} className="ml-1 hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {cityFilter !== "all" && (
              <Badge variant="secondary" className="gap-1">
                <MapPin className="h-3 w-3" />
                {lang === "fr" ? `Départ ${cityFilter}` : `From ${cityFilter}`}
                <button onClick={() => { setCityFilter("all"); setSearchParams(prev => { prev.delete("city"); return prev; }); }} className="ml-1 hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {durationFilter !== "all" && (
              <Badge variant="secondary" className="gap-1">
                <Clock className="h-3 w-3" />
                {durationCategories[durationFilter]?.label?.[lang] || durationCategories[durationFilter]?.label?.fr}
                <button onClick={() => { setDurationFilter("all"); setSearchParams(prev => { prev.delete("duration"); return prev; }); }} className="ml-1 hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* Circuits Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : circuits.length === 0 ? (
          <div className="text-center py-20">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg mb-4">
              {lang === "fr" 
                ? "Aucun circuit ne correspond à vos critères" 
                : "No circuits match your criteria"}
            </p>
            <Button variant="outline" onClick={clearFilters}>
              {lang === "fr" ? "Voir tous les circuits" : "View all circuits"}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {circuits.map((circuit) => (
              <CircuitCard key={circuit.id} circuit={circuit} />
            ))}
          </div>
        )}
      </div>

      {/* FAQ Section - Shows based on active filter */}
      {filter === "4x4" && (
        <FAQSection 
          category="circuits4x4"
          lang={lang}
          title={lang === "fr" ? "Questions fréquentes - Circuits 4x4 Désert Maroc" : "FAQ - Morocco Desert 4x4 Tours"}
        />
      )}
      {filter === "trekking" && (
        <FAQSection 
          category="trek"
          lang={lang}
          title={lang === "fr" ? "Questions fréquentes - Trek Désert Maroc" : "FAQ - Morocco Desert Trek"}
        />
      )}
      {filter === "all" && (
        <FAQSection 
          category="circuits4x4"
          lang={lang}
          title={lang === "fr" ? "Questions fréquentes sur nos circuits" : "Frequently Asked Questions"}
        />
      )}
    </div>
  );
};

export default CircuitsPage;
